import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";

export type ActivityWaitlistRow = Tables<"activity_waitlist">;

export type ActivityWaitlistEntityType =
  | "tournament"
  | "event"
  | "training"
  | "open_match";

export function useActivityWaitlist(
  activityType: ActivityWaitlistEntityType,
  activityId: string | undefined
) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();

  const queryKey = ["activity-waitlist", activityType, activityId];

  // Fetch full waitlist for this activity
  const {
    data: waitlist = [],
    isLoading,
    ...rest
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("activity_waitlist")
        .select("*")
        .eq("activity_type", activityType)
        .eq("activity_id", activityId!)
        .order("position");
      if (error) throw error;
      return (data ?? []) as ActivityWaitlistRow[];
    },
    enabled: !!activityId,
  });

  // Current user's entry
  const myEntry = waitlist.find((e) => e.user_id === user?.id) ?? null;
  const isInQueue = !!myEntry;
  const myPosition = myEntry?.position ?? null;
  const myStatus = myEntry?.status ?? null;
  const waitingCount = waitlist.filter((e) => e.status === "waiting").length;
  const offeredCount = waitlist.filter((e) => e.status === "offered").length;

  const invalidate = () => qc.invalidateQueries({ queryKey });

  // Join waitlist
  const joinMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !organization?.id || !activityId) {
        throw new Error("Saknar användar- eller organisationsinfo");
      }
      // Determine next position
      const maxPos = waitlist.reduce(
        (max, e) => Math.max(max, e.position ?? 0),
        0
      );
      const { error } = await supabase.from("activity_waitlist").insert({
        activity_type: activityType,
        activity_id: activityId,
        user_id: user.id,
        organization_id: organization.id,
        position: maxPos + 1,
        status: "waiting",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Du har ställt dig i kön!");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte gå med i kön");
    },
  });

  // Leave waitlist
  const leaveMutation = useMutation({
    mutationFn: async () => {
      if (!myEntry) throw new Error("Du är inte i kön");
      const { error } = await supabase
        .from("activity_waitlist")
        .update({ status: "cancelled" })
        .eq("id", myEntry.id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Du har lämnat kön");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte lämna kön");
    },
  });

  // Accept offer
  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!myEntry || myEntry.status !== "offered") {
        throw new Error("Inget erbjudande att acceptera");
      }
      const { error } = await supabase
        .from("activity_waitlist")
        .update({ status: "accepted" })
        .eq("id", myEntry.id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Platsen är din!");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte acceptera erbjudandet");
    },
  });

  // Decline offer
  const declineMutation = useMutation({
    mutationFn: async () => {
      if (!myEntry || myEntry.status !== "offered") {
        throw new Error("Inget erbjudande att tacka nej till");
      }
      const { error } = await supabase
        .from("activity_waitlist")
        .update({ status: "cancelled" })
        .eq("id", myEntry.id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Du har tackat nej till erbjudandet");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte tacka nej");
    },
  });

  return {
    waitlist,
    isLoading,
    myEntry,
    isInQueue,
    myPosition,
    myStatus,
    waitingCount,
    offeredCount,
    join: joinMutation.mutate,
    leave: leaveMutation.mutate,
    accept: acceptMutation.mutate,
    decline: declineMutation.mutate,
    isJoining: joinMutation.isPending,
    isLeaving: leaveMutation.isPending,
    isAccepting: acceptMutation.isPending,
    isDeclining: declineMutation.isPending,
    ...rest,
  };
}
